/**
 * Demo AI provider — returns realistic pre-built responses for the
 * investigation pipeline so the UI can be developed and demonstrated
 * without consuming API tokens.
 *
 * Always available. Returns mock data for:
 * - Query decomposition (scope contract JSON)
 * - Entity extraction (entities JSON)
 * - Relationship identification (relationships JSON)
 * - Relevance filtering (score JSON)
 * - Synthesis (markdown assessment)
 *
 * The provider detects which pipeline stage is calling based on keywords
 * in the system/user prompt and returns the appropriate mock payload.
 */

import type {
  AIProviderAdapter,
  AICompletionRequest,
  AICompletionResponse,
} from './base-provider';

/** Simulated latency range (ms) to make the UI feel realistic. */
const MIN_LATENCY = 400;
const MAX_LATENCY = 1200;

function randomLatency(): number {
  return MIN_LATENCY + Math.random() * (MAX_LATENCY - MIN_LATENCY);
}

/** Sleep for a given number of ms. */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Mock response payloads — Baltic maritime scenario (matches ARCHITECTURE.md)
// ---------------------------------------------------------------------------

const MOCK_DECOMPOSITION = JSON.stringify({
  subject: {
    type: 'ASSET',
    subtype: 'vessel',
    description: 'Russian naval vessels and shadow fleet tankers operating in the Baltic Sea region',
    watchlistMatches: [],
  },
  behaviorIndicators: [
    { type: 'unusual_movement', threshold: 'deviation from normal shipping lanes' },
    { type: 'ais_anomaly', threshold: 'AIS transponder gaps > 4 hours' },
    { type: 'proximity', threshold: 'within 12nm of critical infrastructure' },
  ],
  temporalBounds: {
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    end: new Date().toISOString(),
    source: 'query_implied_recent',
  },
  exclusions: ['civilian_ferry_traffic', 'routine_cargo'],
});

const MOCK_ENTITIES = JSON.stringify({
  entities: [
    {
      type: 'ASSET',
      name: 'NS Concord',
      description: 'Russian-flagged tanker suspected of shadow fleet operations in the Baltic',
      aliases: ['NS CONCORD', 'IMO 9305027'],
      properties: { flag: 'Russia', vesselType: 'crude oil tanker', imo: '9305027', mmsi: '273459120' },
      extractionQuote: 'The NS Concord, a Russian-flagged tanker, was observed transiting the Danish Straits with its AIS transponder intermittently active',
      confidence: 0.88,
    },
    {
      type: 'ASSET',
      name: 'Admiral Gorshkov',
      description: 'Russian Navy frigate, Project 22350, Northern Fleet',
      aliases: ['Адмирал Горшков'],
      properties: { flag: 'Russia', vesselType: 'frigate', hullNumber: '454', class: 'Admiral Gorshkov-class' },
      extractionQuote: 'Russian frigate Admiral Gorshkov conducted exercises south of Gotland',
      confidence: 0.92,
    },
    {
      type: 'LOCATION',
      name: 'Gotland',
      description: 'Swedish island in the Baltic Sea, strategic military significance',
      aliases: ['Gotland Island'],
      properties: { country: 'Sweden', coordinates: { lat: 57.5, lng: 18.5 } },
      extractionQuote: 'naval activity was concentrated in waters south of Gotland',
      confidence: 0.95,
    },
    {
      type: 'ORGANIZATION',
      name: 'Russian Northern Fleet',
      description: 'Russian Navy fleet based in Severomorsk, responsible for Arctic and North Atlantic operations',
      aliases: ['Северный флот', 'SF'],
      properties: { headquarters: 'Severomorsk', branch: 'Russian Navy' },
      extractionQuote: 'Northern Fleet assets deployed to the Baltic for scheduled exercises',
      confidence: 0.90,
    },
    {
      type: 'LOCATION',
      name: 'Danish Straits',
      description: 'Maritime chokepoint connecting the Baltic Sea to the North Sea',
      aliases: ['Danish Belts', 'Øresund'],
      properties: { type: 'chokepoint' },
      extractionQuote: 'transiting the Danish Straits with its AIS transponder intermittently active',
      confidence: 0.93,
    },
    {
      type: 'ORGANIZATION',
      name: 'Swedish Armed Forces',
      description: 'Military forces of Sweden, increased Baltic readiness since 2022',
      aliases: ['Försvarsmakten'],
      properties: { country: 'Sweden' },
      extractionQuote: 'Swedish Armed Forces raised readiness level for naval units near Gotland',
      confidence: 0.85,
    },
    {
      type: 'PERSON',
      name: 'Admiral Nikolai Yevmenov',
      description: 'Commander of the Russian Navy',
      aliases: ['Yevmenov'],
      properties: { rank: 'Admiral', role: 'Commander-in-Chief, Russian Navy' },
      extractionQuote: 'Admiral Yevmenov authorized the deployment of additional surface combatants',
      confidence: 0.78,
    },
    {
      type: 'ASSET',
      name: 'Nord Stream Pipeline Route',
      description: 'Subsea gas pipeline route crossing the Baltic Sea',
      aliases: ['Nord Stream'],
      properties: { type: 'infrastructure', status: 'damaged' },
      extractionQuote: 'shadow fleet vessel observed loitering near the Nord Stream pipeline route',
      confidence: 0.82,
    },
    {
      type: 'EVENT',
      name: 'Baltic AIS Disruption Event',
      description: 'Coordinated AIS signal gaps detected across multiple vessels in the central Baltic',
      aliases: [],
      properties: { startDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), affectedVessels: 4 },
      extractionQuote: 'multiple vessels simultaneously lost AIS contact in the central Baltic, consistent with electronic warfare activity',
      confidence: 0.75,
    },
    {
      type: 'LOCATION',
      name: 'Kaliningrad',
      description: 'Russian exclave on the Baltic Sea, major naval base',
      aliases: ['Kaliningrad Oblast'],
      properties: { country: 'Russia', militaryAssets: ['Baltic Fleet HQ', 'Iskander missiles'] },
      extractionQuote: 'vessels departed from Baltiysk naval base in Kaliningrad',
      confidence: 0.91,
    },
    {
      type: 'INDICATOR',
      name: 'Shadow Fleet AIS Pattern',
      description: 'Behavioral indicator: vessels switching AIS on/off at regular intervals to avoid tracking',
      aliases: [],
      properties: { pattern: 'cyclic_ais_gaps', intervalMinutes: 45 },
      extractionQuote: 'characteristic shadow fleet pattern of 45-minute AIS gaps followed by brief transmissions',
      confidence: 0.80,
    },
    {
      type: 'ASSET',
      name: 'Baltiysk Naval Base',
      description: 'Main base of the Russian Baltic Fleet',
      aliases: ['Baltiysk'],
      properties: { country: 'Russia', fleet: 'Baltic Fleet', type: 'naval_base' },
      extractionQuote: 'vessels departed from Baltiysk naval base',
      confidence: 0.94,
    },
  ],
});

const MOCK_RELATIONSHIPS = JSON.stringify({
  relationships: [
    { sourceEntity: 'Admiral Gorshkov', targetEntity: 'Russian Northern Fleet', type: 'part_of', confidence: 0.95, evidenceQuote: 'Northern Fleet frigate Admiral Gorshkov' },
    { sourceEntity: 'Admiral Gorshkov', targetEntity: 'Gotland', type: 'deployed_to', confidence: 0.88, evidenceQuote: 'conducted exercises south of Gotland' },
    { sourceEntity: 'NS Concord', targetEntity: 'Danish Straits', type: 'located_at', confidence: 0.90, evidenceQuote: 'transiting the Danish Straits' },
    { sourceEntity: 'NS Concord', targetEntity: 'Shadow Fleet AIS Pattern', type: 'exhibits', confidence: 0.82, evidenceQuote: 'AIS transponder intermittently active' },
    { sourceEntity: 'NS Concord', targetEntity: 'Nord Stream Pipeline Route', type: 'located_at', confidence: 0.72, evidenceQuote: 'observed loitering near the Nord Stream pipeline route' },
    { sourceEntity: 'Admiral Nikolai Yevmenov', targetEntity: 'Russian Northern Fleet', type: 'commands', confidence: 0.85, evidenceQuote: 'Admiral Yevmenov authorized the deployment' },
    { sourceEntity: 'Swedish Armed Forces', targetEntity: 'Gotland', type: 'deployed_to', confidence: 0.80, evidenceQuote: 'raised readiness level for naval units near Gotland' },
    { sourceEntity: 'Russian Northern Fleet', targetEntity: 'Baltiysk Naval Base', type: 'based_at', confidence: 0.75, evidenceQuote: 'vessels departed from Baltiysk naval base' },
    { sourceEntity: 'Baltic AIS Disruption Event', targetEntity: 'Gotland', type: 'located_at', confidence: 0.78, evidenceQuote: 'central Baltic' },
    { sourceEntity: 'Kaliningrad', targetEntity: 'Baltiysk Naval Base', type: 'contains', confidence: 0.96, evidenceQuote: 'Baltiysk naval base in Kaliningrad' },
  ],
});

const MOCK_RELEVANCE = JSON.stringify({
  score: 0.85,
  matchedIndicatorIds: ['maritime_anomaly', 'military_movement', 'infrastructure_threat'],
});

const MOCK_SYNTHESIS = `## Baltic Maritime Intelligence Assessment

### Summary

Analysis of open-source intelligence indicates heightened Russian naval activity in the Baltic Sea over the past 7 days, centered around the waters south of Gotland and the Danish Straits. Two primary threads of activity have been identified:

1. **Military exercises**: The Northern Fleet frigate Admiral Gorshkov conducted exercises south of Gotland, representing an unusual deployment of a high-capability surface combatant outside its normal operating area.

2. **Shadow fleet activity**: The Russian-flagged tanker NS Concord exhibited characteristic shadow fleet behavior (cyclic AIS gaps at ~45-minute intervals) while transiting the Danish Straits and was subsequently observed loitering near the Nord Stream pipeline route.

### Key Findings

- **12 entities** extracted across 5 sources with corroboration
- **NATO Rating B2** (Usually Reliable / Probably True) for the core military movement assessment
- **NATO Rating C3** (Fairly Reliable / Possibly True) for the shadow fleet infrastructure threat assessment
- Swedish Armed Forces responded by raising readiness levels for naval units near Gotland

### Gaps & Limitations

- No submarine activity data available from open sources
- AIS data has inherent gaps that may not all be intentional
- Limited visibility into Russian military communications

### Recommended Follow-up

- Monitor NS Concord's next port call for cargo verification
- Cross-reference with satellite imagery of Gotland waters
- Track additional Northern Fleet deployments from Severomorsk`;

// ---------------------------------------------------------------------------
// Provider implementation
// ---------------------------------------------------------------------------

export class DemoProvider implements AIProviderAdapter {
  readonly id = 'demo';
  readonly name = 'Demo Mode (No API Tokens)';
  readonly supportsStructuredOutput = false;
  readonly supportsGeneration = true;

  async isAvailable(): Promise<boolean> {
    return true;
  }

  async listModels(): Promise<string[]> {
    return ['demo-haiku', 'demo-sonnet', 'demo-opus'];
  }

  async validate(): Promise<{ valid: boolean; error?: string }> {
    return { valid: true };
  }

  async complete(request: AICompletionRequest): Promise<AICompletionResponse> {
    const latency = randomLatency();
    await sleep(latency);

    const text = this.selectResponse(request);

    return {
      text,
      model: `demo-${request.model ?? 'sonnet'}`,
      provider: 'demo',
      tokensUsed: Math.floor(text.length / 4),
      latencyMs: Math.round(latency),
    };
  }

  /**
   * Detect which pipeline stage is calling based on the SYSTEM prompt.
   *
   * IMPORTANT: Match against the system prompt only, not the user prompt.
   * The user prompt may contain event titles/descriptions from news articles
   * that accidentally contain keywords like "assessment", "entity extraction",
   * or "intelligence brief", causing false matches to the wrong branch.
   */
  private selectResponse(request: AICompletionRequest): string {
    const sys = (request.systemPrompt ?? '').toLowerCase();

    // News classifier — return low-confidence result so keyword classification
    // (which actually reads the content) always wins for real news events.
    // The demo provider can't actually classify — it doesn't read the input.
    if (sys.includes('classifier') || sys.includes('classify')) {
      return JSON.stringify({
        severity: 'info',
        category: 'unknown',
        tags: [],
        confidence: 0.1,
      });
    }

    // Translator — return the original text unchanged (pass-through)
    if (sys.includes('translator') || sys.includes('translate')) {
      return request.prompt ?? '';
    }

    // News summarizer — generate an extractive summary from the actual input
    // rather than returning a hardcoded string for every article
    if (sys.includes('briefing writer') || sys.includes('summarize')) {
      const input = (request.prompt ?? '').trim();
      if (!input) return '';
      // Extract first 1-2 sentences from the input as a crude summary
      const sentences = input.match(/[^.!?]+[.!?]+/g);
      if (sentences && sentences.length > 0) {
        return sentences.slice(0, 2).join(' ').trim();
      }
      // Fallback: truncate to ~200 chars at a word boundary
      if (input.length <= 200) return input;
      const truncated = input.slice(0, 200);
      const lastSpace = truncated.lastIndexOf(' ');
      return (lastSpace > 100 ? truncated.slice(0, lastSpace) : truncated) + '\u2026';
    }

    // Entity extraction
    if (sys.includes('entity extraction') || (sys.includes('extract') && sys.includes('entities'))) {
      return MOCK_ENTITIES;
    }

    // Relationship identification
    if (sys.includes('relationship') && sys.includes('identif')) {
      return MOCK_RELATIONSHIPS;
    }

    // Relevance scoring
    if (sys.includes('relevance') && sys.includes('scor')) {
      return MOCK_RELEVANCE;
    }

    // Query decomposition
    if (sys.includes('scope contract') || sys.includes('decompos')) {
      return MOCK_DECOMPOSITION;
    }

    // Synthesis / assessment
    if (sys.includes('synthesis') || sys.includes('intelligence assessment') || sys.includes('draft assessment')) {
      return MOCK_SYNTHESIS;
    }

    // Default: return empty string — callers handle empty responses gracefully
    return '';
  }
}
